import type { BillingUsage } from "@nlkb/api-contract/billing";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useId, useState } from "react";
import { useNavigate } from "react-router";

import {
  afterBillingCompletePath,
  billingPeriodLabel,
  billingPlanPriceLabel,
  formatBillingDay,
  includedCreditProgressPercent,
} from "../account-billing";
import { useApi } from "../api-context";
import {
  creditPackPurchaseLabel,
  ingestCreditPackFootnote,
  resumeSubscriptionLabel,
} from "../billing-copy";
import {
  desktopHostedCheckoutCopy,
  followHostedHttpsUrl,
} from "../hosted-https";
import { publicListPricesUsd, teamsMonthlyUsd } from "../list-prices";
import { AppSelect } from "./AppSelect";
import ConfirmDialog from "./ConfirmDialog";

type SelfServePlan = "solo" | "teams";
type BillingConfirm = "plan" | "credits" | null;

export function AccountBillingPanel({
  usage,
  organizationId,
}: {
  readonly usage: BillingUsage;
  readonly organizationId: string;
}) {
  const api = useApi();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const progressId = useId();
  const footnoteId = useId();
  const [hostedCopied, setHostedCopied] = useState(false);
  const [confirm, setConfirm] = useState<BillingConfirm>(null);
  const [planDraft, setPlanDraft] = useState<SelfServePlan>(
    usage.plan === "teams" ? "teams" : "solo",
  );
  const [seatDraft, setSeatDraft] = useState(
    Math.max(2, usage.seat_used, usage.max_seats ?? 2),
  );
  const percent = includedCreditProgressPercent(
    usage.included_credits_used,
    usage.included_credits_per_period,
  );
  const price = billingPlanPriceLabel(usage.plan, usage.max_seats);
  const canMutateCustomerBilling =
    usage.role === "org_owner" ||
    usage.role === "partner_owner" ||
    usage.role === "partner_admin";
  const showPortal =
    canMutateCustomerBilling &&
    usage.billing_mode === "stripe" &&
    usage.has_stripe_customer &&
    usage.plan !== "platform";
  const showCreditPacks =
    canMutateCustomerBilling &&
    usage.plan !== "platform" &&
    usage.status !== "canceled";
  const canResume = canMutateCustomerBilling && usage.status === "canceled";
  const ownerBilled = usage.role === "org_owner";
  const canEditSelfServePlan =
    ownerBilled &&
    (usage.plan === "solo" || usage.plan === "teams") &&
    usage.status === "active";
  const planUnchanged =
    planDraft === usage.plan &&
    (planDraft === "solo" || seatDraft === usage.max_seats);

  const refresh = async () => {
    await queryClient.invalidateQueries({ queryKey: ["billing-usage"] });
  };

  const buyCredits = useMutation({
    mutationFn: async () => {
      const checkout = await api.createCreditsCheckout(
        usage.billing_account_id,
        organizationId.length > 0 ? organizationId : undefined,
      );
      if (checkout.billing_mode === "local" || checkout.checkout_url === null) {
        return api.completeBillingCheckout(checkout.session_id);
      }
      const follow = await followHostedHttpsUrl(checkout.checkout_url);
      setHostedCopied(follow === "copied");
      return checkout;
    },
    onSuccess: () => {
      void refresh();
    },
  });
  const openPortal = useMutation({
    mutationFn: () =>
      api.billingPortal(
        usage.billing_account_id,
        organizationId.length > 0 ? organizationId : undefined,
      ),
    onSuccess: async (result) => {
      const follow = await followHostedHttpsUrl(result.portal_url);
      setHostedCopied(follow === "copied");
    },
  });
  const resume = useMutation({
    mutationFn: async () => {
      const checkout = await api.createResubscribeCheckout(
        usage.billing_account_id,
        organizationId.length > 0 ? organizationId : undefined,
      );
      if (checkout.billing_mode === "local" || checkout.checkout_url === null) {
        return api.completeBillingCheckout(checkout.session_id);
      }
      const follow = await followHostedHttpsUrl(checkout.checkout_url);
      setHostedCopied(follow === "copied");
      return checkout;
    },
    onSuccess: async (result) => {
      await refresh();
      if ("kind" in result) {
        await queryClient.invalidateQueries({ queryKey: ["organizations"] });
        void navigate(afterBillingCompletePath(result));
      }
    },
  });
  const changePlan = useMutation({
    mutationFn: () =>
      api.changeBillingPlan(
        usage.billing_account_id,
        planDraft,
        planDraft === "teams" ? seatDraft : undefined,
        organizationId.length > 0 ? organizationId : undefined,
      ),
    onSuccess: async () => {
      await refresh();
      await queryClient.invalidateQueries({ queryKey: ["organizations"] });
    },
  });
  const updateSeats = useMutation({
    mutationFn: () =>
      api.changeBillingSeats(
        usage.billing_account_id,
        seatDraft,
        organizationId.length > 0 ? organizationId : undefined,
      ),
    onSuccess: async () => {
      await refresh();
      await queryClient.invalidateQueries({ queryKey: ["organizations"] });
    },
  });
  const actionError =
    buyCredits.error ??
    openPortal.error ??
    resume.error ??
    changePlan.error ??
    updateSeats.error;
  const planBusy = changePlan.isPending || updateSeats.isPending;
  const planConfirm =
    planDraft === "solo" && usage.plan === "teams"
      ? {
          title: "Switch to Solo?",
          description:
            "Solo is $8/month for one person. Remove extra people, groups, and team vaults first. The change is prorated.",
          confirmLabel: "Switch to Solo",
        }
      : planDraft === "teams" && usage.plan === "solo"
        ? {
            title: "Upgrade to Teams?",
            description: `Teams is $${String(teamsMonthlyUsd(seatDraft))}/month for ${String(seatDraft)} seats. The change is prorated on the current subscription.`,
            confirmLabel: "Upgrade to Teams",
          }
        : {
            title: "Update seats?",
            description: `This workspace will use ${String(seatDraft)} seats at $${String(teamsMonthlyUsd(seatDraft))}/month. The change is prorated.`,
            confirmLabel: "Update seats",
          };

  return (
    <section
      className="account-billing"
      aria-labelledby={`${progressId}-title`}
    >
      <div className="account-billing__heading">
        <p className="utility-label">Plan</p>
        <p className="account-billing__plan" id={`${progressId}-title`}>
          {usage.plan}
        </p>
      </div>
      <p className="billing-meter__note">
        {price === null ? null : `${price}. `}
        {usage.status.replaceAll("_", " ")}.{" "}
        {billingPeriodLabel(
          usage.plan,
          usage.period_start,
          usage.period_end,
          usage.status,
        )}
        .
      </p>
      {usage.plan === "teams" ? (
        <p className="billing-meter__note">
          Seats {String(usage.seat_used)}
          {usage.max_seats === null ? "" : ` / ${String(usage.max_seats)}`}
        </p>
      ) : null}
      {canEditSelfServePlan ? (
        <div className="account-billing__plan-edit">
          <label>
            <span className="utility-label">Edit plan</span>
            <AppSelect
              id={`${progressId}-plan`}
              aria-label="Edit plan"
              value={planDraft}
              options={[
                { value: "solo", label: "Solo" },
                { value: "teams", label: "Teams" },
              ]}
              onChange={(value) => {
                const next = value === "teams" ? "teams" : "solo";
                setPlanDraft(next);
                if (next === "teams") {
                  setSeatDraft(
                    Math.max(2, usage.seat_used, usage.max_seats ?? 2),
                  );
                }
              }}
            />
          </label>
          {planDraft === "teams" ? (
            <label className="account-billing__seats">
              <span className="utility-label">Seats</span>
              <input
                type="number"
                min={Math.max(2, usage.seat_used)}
                max={500}
                value={seatDraft}
                onChange={(event) => {
                  setSeatDraft(Number(event.target.value));
                }}
              />
            </label>
          ) : null}
          <button
            className="primary-action"
            type="button"
            disabled={planUnchanged || planBusy}
            onClick={() => {
              setConfirm("plan");
            }}
          >
            {planBusy ? "Saving…" : "Change plan"}
          </button>
        </div>
      ) : null}
      <div>
        <p className="utility-label" id={`${progressId}-label`}>
          Included credits this period
        </p>
        <div
          className="account-billing__track"
          role="progressbar"
          aria-labelledby={`${progressId}-label`}
          aria-valuemin={0}
          aria-valuemax={usage.included_credits_per_period}
          aria-valuenow={usage.included_credits_used}
          aria-valuetext={`${String(usage.included_credits_used)} of ${String(usage.included_credits_per_period)} included credits`}
        >
          <div
            className="account-billing__fill"
            style={{ width: `${String(percent)}%` }}
          />
        </div>
        <p className="billing-meter__note">
          {String(usage.included_credits_used)} /{" "}
          {String(usage.included_credits_per_period)} used. Resets{" "}
          {formatBillingDay(usage.period_end)}.
        </p>
        <p className="billing-meter__note">
          {String(usage.purchased_credits)} extra credits remaining.
        </p>
      </div>
      <div className="team-toolbar__actions">
        {canResume ? (
          <button
            className="primary-action"
            type="button"
            disabled={resume.isPending}
            onClick={() => {
              resume.mutate();
            }}
          >
            {resume.isPending ? "Opening…" : resumeSubscriptionLabel}
          </button>
        ) : null}
        {showCreditPacks ? (
          <button
            className="primary-action"
            type="button"
            disabled={buyCredits.isPending}
            aria-describedby={footnoteId}
            onClick={() => {
              setConfirm("credits");
            }}
          >
            {buyCredits.isPending
              ? "Buying…"
              : creditPackPurchaseLabel(publicListPricesUsd.creditPack100)}
          </button>
        ) : null}
        {showPortal ? (
          <button
            className="secondary-action"
            type="button"
            disabled={openPortal.isPending}
            onClick={() => {
              openPortal.mutate();
            }}
          >
            {openPortal.isPending ? "Opening…" : "Manage billing"}
          </button>
        ) : null}
      </div>
      <p id={footnoteId} className="billing-meter__note">
        {showCreditPacks ? `* ${ingestCreditPackFootnote}` : null}
      </p>
      {hostedCopied ? <p role="status">{desktopHostedCheckoutCopy}</p> : null}
      {actionError === null ? null : (
        <p className="form-error" role="alert">
          {actionError.message.replaceAll("_", " ")}
        </p>
      )}
      <ConfirmDialog
        open={confirm === "plan"}
        label="Billing"
        title={planConfirm.title}
        description={planConfirm.description}
        confirmLabel={planConfirm.confirmLabel}
        cancelLabel="Cancel"
        confirmTone="primary"
        onCancel={() => {
          setConfirm(null);
        }}
        onConfirm={() => {
          setConfirm(null);
          if (planDraft === usage.plan) {
            updateSeats.mutate();
            return;
          }
          changePlan.mutate();
        }}
      />
      <ConfirmDialog
        open={confirm === "credits"}
        label="Billing"
        title="Buy 100 credits?"
        description={`This adds 100 extra ingest credits for $${String(publicListPricesUsd.creditPack100)}. ${ingestCreditPackFootnote}`}
        confirmLabel="Buy credits"
        cancelLabel="Cancel"
        confirmTone="primary"
        onCancel={() => {
          setConfirm(null);
        }}
        onConfirm={() => {
          setConfirm(null);
          buyCredits.mutate();
        }}
      />
    </section>
  );
}
